<template>
  <form @submit.prevent="onSubmit">
    <!-- delete confirm -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ----------------- -->
    <!-- TOP ROW (HEADER)  -->
    <!-- ----------------- -->
    <div class="row h-40">
      <p class="bb-page-title mt-2">SKILL DETAILS</p>
    </div>
    <!-- ------------------------------------------------------------>
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row mt-1 mb-1 m-2"
    >
      <!-- ------------------------------------------------------------>
      <!--    LEFT COL    -->
      <!-- -------------- -->
      <div class="col-3">
        <!-- ---------- -->
        <!-- BUTTONS    -->
        <!-- ---------- -->
        <div class="row mt-2">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="manageCancel()"
            >
              Cancel
            </button>
            <!-- ------------ -->
            <!--    delete    -->
            <!-- ------------ -->
            <!-- cannot delete skills where: new pending skill, protected casting skill, or default skill -->
            <button
              v-if="canDeleteSkill && !this.delete"
              class="btn btn-primary btn-lg"
              type="button"
              @click="confirmDeletion()"
            >
              Delete
            </button>
            <!-- ------------ -->
            <!--     add      -->
            <!-- ------------ -->
            <button
              v-if="isNewSkill && activeSkill.name != ''"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <!-- ------------ -->
            <!--      ok      -->
            <!-- ------------ -->
            <button
              v-if="!isNewSkill"
              :disabled="!isChanged"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              OK
            </button>
            <button
              v-if="documentViewerUrl"
              class="btn btn-primary btn-lg"
              type="button"
              @click="openDocument()"
            >
              Document
            </button>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!--    RIGHT COL    -->
      <!-- -------------- -->
      <div class="col m-3 bb-tooltip-wrapper">
        <!-- ---------- -->
        <!--    type    -->
        <!-- ---------- -->
        <p class="bb-hdg-lg mb-2 mt-1">
          {{
            skillType && skillName ? skillType + ' - ' + skillName : skillType
          }}
        </p>
        <!-- spacer -->
        <div class="row mt-1 mb-1"></div>
        <!-- -------------- -->
        <!--      ROW 1     -->
        <!-- -------------- -->
        <div class="row mt-2 mb-2">
          <!-- --------------------- -->
          <!--   row number (r/o)    -->
          <!-- --------------------- -->
          <div
            v-if="activeSkill.id !== 0"
            class="col-3"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  v-model="rowNumber"
                  id="row"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="row"
                  >Row</label
                >
              </div>
            </div>
          </div>
          <!-- ------------------- -->
          <!--   skill name (r/o)  -->
          <!-- ------------------- -->
          <div
            v-if="activeSkill.id !== 0"
            class="col"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="skillName"
                  id="skill_name"
                  disabled
                />
                <label
                  :class="[
                    'bb-float-label',
                    { optional: activeSkill.id !== 0 },
                  ]"
                  for="skill_name"
                  >Skill Name</label
                >
              </div>
            </div>
          </div>
          <!-- --------------------- -->
          <!--   skill name (input)  -->
          <!-- --------------------- -->
          <div
            v-if="activeSkill.id === 0"
            class="col"
          >
            <div class="form-group">
              <div class="form-floating">
                <v-select
                  :options="new_skill_option_names"
                  class="form-control"
                  :value="activeSkill.name"
                  @keydown="onKeyDown($event)"
                  @update:modelValue="onSkillSelect"
                  id="skill_name"
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      :required="!activeSkill.name"
                      v-bind="attributes"
                      v-on="events"
                    />
                  </template>
                </v-select>
                <label
                  class="bb-float-label"
                  for="skill_name"
                  >Skill Name</label
                >
              </div>
            </div>
          </div>
          <!-- --------------------- -->
          <!--   parent skill (r/o)  -->
          <!-- --------------------- -->
          <div
            v-if="hasParent"
            class="col"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="activeSkill.parent_name"
                  id="parent"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="parent"
                  >Parent Skill</label
                >
              </div>
            </div>
          </div>
        </div>
        <!-- -------------- -->
        <!--      ROW 2     -->
        <!-- -------------- -->
        <div
          v-if="hasSkill"
          class="row mt-2 mb-2"
        >
          <!-- ------------------ -->
          <!--   formula (r/o)    -->
          <!-- ------------------ -->
          <div class="col form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="activeSkill.formula"
                id="formula"
                disabled
              />
              <label
                class="bb-float-label"
                for="formula"
                >Formula</label
              >
            </div>
          </div>
          <!-- ------------------ -->
          <!--   base (r/o)       -->
          <!-- ------------------ -->
          <div
            v-if="!hasParent"
            class="col form-group"
          >
            <ToolTip
              :text="displayCalcBreakdown('base')"
              position="bottom"
              :width="skillTooltipWidth"
              :max-width="skillTooltipMaxWidth"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="display_base"
                  id="base"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="base"
                  >Base
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <!-- ------------------ -->
          <!--   tickable (r/o)   -->
          <!-- ------------------ -->
          <div class="col form-group">
            <div class="form-floating">
              <input
                readonly
                class="form-control text-center"
                :value="display_tickable"
                id="tickable"
                disabled
              />
              <label
                class="bb-float-label"
                for="tickable"
                >Tickable</label
              >
            </div>
          </div>
        </div>
        <!-- ---------------------------- -->
        <!--   WHERE SKILL NAME PRESENT   -->
        <!-- ---------------------------- -->
        <div v-if="activeSkill.name !== ''">
          <!-- ---------------- -->
          <!--   IMPROVEMENTS   -->
          <!-- ---------------- -->
          <p
            v-if="!hasParent"
            class="bb-sub-hdg mb-2 mt-2"
          >
            Improvements
          </p>
          <!-- -------------- -->
          <!--      ROW 3     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- -------------------- -->
            <!--      ACTIVE SKILL    -->
            <!-- -------------------- -->
            <template v-if="isActiveSkill">
              <!-- ---------------- -->
              <!--   ticks (input)  -->
              <!-- ---------------- -->
              <div
                v-if="!hasParent && local_skill"
                class="col"
              >
                <CounterEmit
                  :pass_ref="'ticks'"
                  :pass_label="'Ticks'"
                  :pass_count="local_skill.ticks"
                  :pass_min="config.min_percent"
                  :pass_max="activeSkill.ticks_max"
                  :pass_digits="maxTicksDigits"
                  :disabled="isTicksMaxed || isMaxSkill || isCurrentSkillCapped"
                  @change="onCounterChanged"
                />
              </div>
              <!-- ------------------- -->
              <!--   training (input)  -->
              <!-- ------------------- -->
              <div
                v-if="!hasParent && local_skill"
                class="col"
              >
                <CounterEmit
                  :pass_ref="'training'"
                  :pass_label="'Training'"
                  :pass_count="local_skill.training"
                  :pass_min="config.min_percent"
                  :pass_max="maxTraining"
                  :pass_digits="maxTrainingDigits"
                  :disabled="isMaxSkill || isCurrentSkillCapped"
                  @change="onCounterChanged"
                />
              </div>
            </template>
            <!-- -------------------- -->
            <!--    INACTIVE SKILL    -->
            <!-- -------------------- -->
            <template v-else>
              <!-- --------------- -->
              <!--   ticks (r/o)   -->
              <!-- --------------- -->
              <div class="col-6 form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_skill.ticks"
                    id="ticks"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="ticks"
                    >Ticks</label
                  >
                </div>
              </div>
              <!-- ------------------ -->
              <!--   training (r/o)   -->
              <!-- ------------------ -->
              <div class="col-6 form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_skill.training"
                    id="training"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="training"
                    >Training</label
                  >
                </div>
              </div>
            </template>
          </div>
          <!-- spacer -->
          <div class="row mt-2 mb-2"></div>
          <!-- -------------- -->
          <!--      ROW 4     -->
          <!-- -------------- -->
          <div
            v-if="!hasParent"
            class="row mt-2 mb-2"
          >
            <!-- ------------------- -->
            <!--   ticks max (r/o)   -->
            <!-- ------------------- -->
            <div class="col form-group">
              <ToolTip
                :text="displayCalcBreakdown('ticks_max')"
                position="bottom"
                :width="skillTooltipWidth"
                :max-width="skillTooltipMaxWidth"
              >
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="activeSkill.ticks_max"
                    id="ticks_max"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="ticks_max"
                    >Max Ticks
                    <span class="bb-orange-circle"></span>
                  </label>
                </div>
              </ToolTip>
            </div>
            <!-- ------------------------- -->
            <!--   ticks remaining (r/o)   -->
            <!-- ------------------------- -->
            <div class="col form-group">
              <ToolTip
                :text="displayCalcBreakdown('ticks_remaining')"
                position="bottom"
                :width="skillTooltipWidth"
                :max-width="skillTooltipMaxWidth"
              >
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="ticksRemaining"
                    id="ticks_remaining"
                    disabled
                  />
                  <label
                    class="bb-float-label"
                    for="ticks_remaining"
                    >Ticks Remaining
                    <span class="bb-orange-circle"></span>
                  </label>
                </div>
              </ToolTip>
            </div>
            <!-- ---------------------- -->
            <!--   capped ticks (r/o)   -->
            <!-- ---------------------- -->
            <div class="col form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="display_capped_ticks"
                  id="capped_ticks"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="capped_ticks"
                  >Capped Ticks</label
                >
              </div>
            </div>
          </div>

          <!-- -------------- -->
          <!--      ROW 5     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- A. WHERE PARENT  -->
            <template v-if="hasParent">
              <!-- COL 1 -->
              <div class="col-3">
                <!-- <p class="bb-sub-hdg mb-2 mt-2">&#8203;</p> -->
                <!-- ---------------- -->
                <!--   PARENT         -->
                <!-- ---------------- -->
                <p class="bb-sub-hdg mb-2 mt-2">Parent</p>
                <!-- -------------------- -->
                <!--   parent cur (r/o)   -->
                <!-- -------------------- -->
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control text-center"
                      :value="activeSkill.parent_cur"
                      id="parent_cur"
                      disabled
                    />
                    <label
                      class="bb-float-label"
                      for="parent_cur"
                      >Parent Cur</label
                    >
                  </div>
                </div>
              </div>

              <!-- COL 2 -->
              <div class="col-3">
                <!-- ---------------- -->
                <!--   ADJUSTMENTS    -->
                <!-- ---------------- -->
                <p class="bb-sub-hdg mb-2 mt-2">Adjustments</p>
                <!-- ----------------------------- -->
                <!--   applied adjs total (r/o)    -->
                <!-- ----------------------------- -->
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control text-center"
                      :value="activeSkill.adjs_tot"
                      id="adjs_tot"
                      disabled
                    />
                    <label
                      class="bb-float-label"
                      for="adjs_tot"
                      >Applied Adjs Total</label
                    >
                  </div>
                </div>
              </div>

              <!-- COL 3 -->
              <div class="col-3">
                <!-- ------------ -->
                <!--   CURRENT    -->
                <!-- ------------ -->
                <p class="bb-sub-hdg mb-2 mt-2">Current</p>
                <!-- -------------- -->
                <!--   cur (r/o)    -->
                <!-- -------------- -->
                <div class="form-group">
                  <ToolTip
                    :text="displayCalcBreakdown('cur')"
                    position="top"
                    :width="skillTooltipWidth"
                    :max-width="skillTooltipMaxWidth"
                  >
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="activeSkill.cur"
                        id="cur"
                        disabled
                        style="
                          color: #25bfda !important;
                          font-size: 2vw !important;
                        "
                      />
                      <label
                        class="bb-float-label"
                        for="cur"
                        >Cur
                        <span class="bb-orange-circle"></span>
                      </label>
                    </div>
                  </ToolTip>
                </div>
              </div>

              <!-- COL 4 (spacer) -->
              <div class="col-3">
                <p class="bb-sub-hdg mb-2 mt-2">&#8203;</p>
              </div>
            </template>

            <!-- B. WHERE NO PARENT  -->
            <template v-else>
              <!-- COL 1 -->
              <div class="col-3">
                <!-- ---------------- -->
                <!--   ADJUSTMENTS    -->
                <!-- ---------------- -->
                <p class="bb-sub-hdg mb-2 mt-2">Adjustments</p>
                <!-- --------------------- -->
                <!--   adjs total (r/o)    -->
                <!-- --------------------- -->
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      readonly
                      class="form-control text-center"
                      :value="activeSkill.adjs_tot"
                      id="adjs_tot"
                      disabled
                    />
                    <label
                      class="bb-float-label"
                      for="adjs_tot"
                      >Applied Adjs</label
                    >
                  </div>
                </div>
              </div>

              <!-- COL 2 -->
              <div class="col-3">
                <!-- ------------ -->
                <!--   CURRENT    -->
                <!-- ------------ -->
                <p class="bb-sub-hdg mb-2 mt-2">Current</p>
                <!-- ------------------------- -->
                <!--   cur no adjs (r/o)     -->
                <!-- ------------------------- -->
                <div class="form-group">
                  <ToolTip
                    :text="displayCalcBreakdown('cur_no_adjs')"
                    position="top"
                    :width="skillTooltipWidth"
                    :max-width="skillTooltipMaxWidth"
                  >
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="activeSkill.cur_no_adjs"
                        id="cur_no_adjs"
                        disabled
                      />
                      <label
                        class="bb-float-label"
                        for="cur_no_adjs"
                        >Cur No Adjs
                        <span class="bb-orange-circle"></span>
                      </label>
                    </div>
                  </ToolTip>
                </div>
              </div>

              <!-- COL 4 -->
              <div class="col-3">
                <p class="bb-sub-hdg mb-2 mt-2">&#8203;</p>
                <!-- ------------------- -->
                <!--   increase (r/o)    -->
                <!-- ------------------- -->
                <div
                  v-if="!hasParent"
                  class="form-group"
                >
                  <ToolTip
                    :text="displayCalcBreakdown('incr')"
                    position="top"
                    :width="skillTooltipWidth"
                    :max-width="skillTooltipMaxWidth"
                  >
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="activeSkill.incr_percent"
                        id="incr_percent"
                        disabled
                      />
                      <label
                        class="bb-float-label"
                        for="incr_percent"
                        >Increase Roll
                        <span class="bb-orange-circle"></span>
                      </label>
                    </div>
                  </ToolTip>
                </div>
              </div>

              <!-- COL 3 -->
              <div class="col-3">
                <p class="bb-sub-hdg mb-2 mt-2">&#8203;</p>
                <!-- --------------- -->
                <!--   cur (r/o)     -->
                <!-- --------------- -->
                <div class="form-group">
                  <ToolTip
                    :text="displayCalcBreakdown('cur')"
                    position="top"
                    :width="skillTooltipWidth"
                    :max-width="skillTooltipMaxWidth"
                  >
                    <div class="form-floating">
                      <input
                        readonly
                        class="form-control text-center"
                        :value="activeSkill.cur"
                        id="cur"
                        disabled
                        style="
                          color: #25bfda !important;
                          font-size: 2vw !important;
                        "
                      />
                      <label
                        class="bb-float-label"
                        for="cur"
                        >Cur
                        <span class="bb-orange-circle"></span>
                      </label>
                    </div>
                  </ToolTip>
                </div>
              </div>
            </template>
          </div>
          <!-- ------------- -->
          <!--     ROW 6     -->
          <!-- ------------- -->
          <div
            v-if="activeSkill.description"
            class="row mt-2 mb-2"
          >
            <!-- ----------------- -->
            <!--   DESCRIPTIONS    -->
            <!-- ----------------- -->
            <p class="bb-sub-hdg mb-2 mt-2">Descriptions</p>
            <!-- -------------------- -->
            <!--  description (r/o)   -->
            <!-- -------------------- -->
            <div class="form-group">
              <div class="form-floating">
                <textarea
                  id="description"
                  name="description"
                  type="text"
                  rows="6"
                  class="form-control bb-textarea-with-label bb-card-description-control bb-skill-description-control"
                  :value="activeSkill.description || ''"
                  disabled
                ></textarea>
                <label
                  class="bb-float-label optional"
                  for="description"
                  >Description</label
                >
              </div>
            </div>
          </div>
        </div>
        <!-- ---------- -->
        <!--    ERRORS  -->
        <!-- ---------- -->
        <div
          v-if="errors.length > 0"
          class="row mt-2 mb-2"
        >
          <div class="form-group">
            <!-- ------------ -->
            <!--    errors    -->
            <!-- ------------ -->
            <div
              v-for="err in errors"
              :key="err.id"
              class="bb-error"
            >
              {{ err }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import ConfirmDialog from '../components/ConfirmDialog.vue'
import ToolTip from '../components/ToolTip.vue'
import CounterEmit from './CounterEmit.vue'

import { mapGetters } from 'vuex'
import { inject } from 'vue'

import getPopulatedSkillCurNoAdjs from '../helpers/getPopulatedSkillCurNoAdjs'
import getPopulatedSkillIncrease from '../helpers/getPopulatedSkillIncrease'
import getPopulatedSkillCurrent from '../helpers/getPopulatedSkillCurrent'
import getPopulatedSkillBase from '../helpers/getPopulatedSkillBase'

import calcSkillValues from '../helpers/calcSkillValues'
import isObjectChanged from '../helpers/isObjectChanged'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import setSkillValues from '../helpers/setSkillValues'
import isAlphanumeric from '../helpers/isAlphanumeric'
import extractNames from '../helpers/extractNames'
import createTempId from '../helpers/createTempId'
import isEmpty from '../helpers/isEmpty'

export default {
  name: 'SkillCard',
  emits: [
    'add-character-skill',
    'update-character-skill',
    'delete-character-skill',
    'close-skill-panel',
  ],
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    CounterEmit,
    ToolTip,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // props
  //-------
  props: {
    skill: {
      type: Object,
      default: null,
    },
    activeTab: {
      type: String,
      required: true,
    },
    character: {
      type: Object,
      required: true,
      default: null,
    },
    max_skill: {
      type: Number,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      config: null,
      original_skill: null,
      original_skill_snapshot: { ticks: 0, training: 0 },
      //
      local_skill: {},
      //
      errors: [],
      errorFields: [],
      new_skill_options: [],
      //
      save_new_skill_id: null,
      //
      original_skill_name: '',
      //
      submitCommitted: false,
      //
      delete: false,
      // ticks
      ticks_min: 0,
      ticks_max: 99,
      ticks_digits: 2,
      // training
      training_min: 0,
      training_digits: 2,
      // drop-downs
      new_skill_option_names: [],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      handler() {
        this.refreshLocalSkillDerivedValues()
      },
      deep: true,
    },
    skill: {
      handler(newSkill) {
        if (!newSkill) return

        this.local_skill = copyObjectNoRef(newSkill) || {}
        this.refreshLocalSkillDerivedValues()
      },
      deep: true,
    },
  },
  //==========================================
  // computed
  //-----------
  computed: {
    //--------
    // mapped
    //--------
    ...mapGetters('characters', {
      getCharacterSkills: 'getCharacterSkills',
      getStaticCommon: 'getStaticCommon',
      getStaticUncommon: 'getStaticUncommon',
      getStaticSpecialisms: 'getStaticSpecialisms',
      getStaticLanguages: 'getStaticLanguages',
      getStaticLores: 'getStaticLores',
      getStaticMeleeWeapons: 'getStaticMeleeWeapons',
      getStaticRangedWeapons: 'getStaticRangedWeapons',
      getStaticCasting: 'getStaticCasting',
    }),
    ticksRemaining() {
      const remaining =
        (this.activeSkill?.ticks_max ?? 0) - (this.activeSkill?.ticks ?? 0)
      // console.log('ticksRemaining calc remaining = ', remaining, ' for local_skill.cur_no_adjs = ', this.local_skill?.cur_no_adjs)
      return remaining
    },
    maxTraining() {
      const max =
        (this.activeSkill?.training ?? 0) +
        this.max_skill -
        (this.activeSkill?.cur_no_adjs ?? 0)
      // console.log('maxTraining calc max = ', max, ' for local_skill.cur_no_adjs = ', this.local_skill?.cur_no_adjs)
      return max
    },
    maxTrainingDigits() {
      const max = this.maxTraining ?? 0
      return max.toString().length
    },
    maxTicksDigits() {
      const max = this.activeSkill?.ticks_max ?? 0
      return max.toString().length
    },
    skillTooltipWidth() {
      return '250px'
    },
    skillTooltipMaxWidth() {
      return '250px'
    },
    isReady() {
      return !isEmpty(this.activeSkill) && !isEmpty(this.local_skill)
    },
    activeSkill() {
      // existing or new skill
      const hasLocal =
        !isEmpty(this.local_skill) && Object.keys(this.local_skill).length > 0
      return hasLocal ? this.local_skill : (this.skill ?? {})
    },
    skillType() {
      console.log(
        'skillType calc for activeTab = ',
        this.activeTab,
        ' and activeSkill = ',
        this.activeSkill,
      )
      const s = this.activeSkill

      // Where we have a named skill, use its data
      if (s && s.name) {
        if (s.name === 'Runecasting') {
          if (!this.hasLinkedBackingRune) {
            return 'Runecasting (No Rune)'
          }

          return this.isActiveSkill ? 'Runecasting' : 'Runecasting (Inactive)'
        }
        if (s.type) {
          if (this.isWeaponSkillInactive) {
            return `${s.type} (Inactive)`
          }

          return s.type
        }
      }

      // For new/unnamed skills, fall back to the active tab
      if (this.activeTab === 'skills-specialisms') {
        return 'Specialism'
      } else if (this.activeTab === 'skills-languages') {
        return 'Language'
      } else if (this.activeTab === 'skills-lores') {
        return 'Lore'
      } else if (this.activeTab === 'skills-melee-weapons') {
        return 'Melee'
      } else if (this.activeTab === 'skills-ranged-weapons') {
        return 'Ranged'
      }
      return ''
    },
    skillName() {
      const s = this.activeSkill || {}
      if (!s) return ''

      // For Runecasting and Sorcery Casting, prefer the per-instance display_name
      if (s.name === 'Runecasting' || s.name === 'Sorcery Casting') {
        return s.display_name || s.name
      }

      // Fallback: use the underlying skill name
      return s.name || ''
    },
    documentViewerUrl() {
      return this.buildAbsoluteUrl(this.activeSkill?.reference_document ?? '')
    },
    isProtectedCastingSkill() {
      const skillName = this.activeSkill?.name || ''
      if (skillName === 'Sorcery Casting') {
        return true
      }

      if (skillName === 'Runecasting') {
        return this.hasCharacterRuneLink
      }

      return false
    },
    hasCharacterRuneLink() {
      const skill = this.activeSkill || {}

      return skill.character_rune != null || skill.character_rune_id != null
    },
    hasLinkedBackingRune() {
      const s = this.activeSkill || {}
      if (s.type !== 'Rune') return true

      const runeName = s.display_name || s.name || ''
      if (!runeName) return false

      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []
      return runes.some((r) => r.name === runeName)
    },
    hasActiveBackingRune() {
      const s = this.activeSkill || {}
      if (s.type !== 'Rune') return true

      const runeName = s.display_name || s.name || ''
      if (!runeName) return false

      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []
      return runes.some(
        (r) =>
          r.name === runeName && r.integrated === true && r.active === true,
      )
    },
    isWeaponSkillInactive() {
      const skillType = this.activeSkill?.type
      const isWeaponSkill = skillType === 'Melee' || skillType === 'Ranged'

      if (!isWeaponSkill) {
        return false
      }

      const usableStatus = this.activeSkill?.usable_status
      return usableStatus === 'No' || usableStatus === 'Partial'
    },
    isActiveSkill() {
      if (this.isWeaponSkillInactive) {
        return false
      }

      return this.activeSkill.type !== 'Rune' || this.hasActiveBackingRune
    },
    isNewSkill() {
      return !this.activeSkill || this.activeSkill.id === 0
    },
    canDeleteSkill() {
      return (
        !this.isNewSkill &&
        !this.isProtectedCastingSkill &&
        !this.activeSkill.default
      )
    },
    isMaxSkill() {
      return (
        !this.activeSkill || this.activeSkill.cur_no_adjs === this.max_skill
      )
    },
    hasSkill() {
      const s = this.activeSkill
      if (!s || !s.name) return false
      return true
    },
    hasParent() {
      const s = this.activeSkill
      if (!s || !s.name) return false
      return !isEmpty(s.parent_name)
    },
    rowNumber() {
      return (this.activeSkill?.originalIndex ?? 0) + 1
    },
    display_base() {
      return (this.activeSkill?.base ?? 0) === 0
        ? ''
        : (this.activeSkill.base ?? '')
    },
    display_tickable() {
      const s = this.activeSkill
      if (!s || !s.name) return ''
      return s.tickable === true && !this.hasParent ? 'Yes' : 'No'
    },
    display_capped_ticks() {
      const s = this.activeSkill
      if (!s || !s.name) return ''
      return s.ticks === s.ticks_max ? 'Yes' : 'No'
    },
    isTicksMaxed() {
      const s = this.activeSkill
      return s && s.ticks === s.ticks_max
    },
    isCurrentSkillCapped() {
      const currentValue = Number(this.activeSkill?.cur ?? 0)
      return currentValue === 100
    },
    hasName() {
      const name = this.activeSkill?.name ?? ''
      return name !== ''
    },
    isChanged() {
      // new skills...
      if (this.isNewSkill) {
        return isObjectChanged(this.original_skill, this.skill)
      }
      // existing skills...
      return (
        (this.local_skill?.ticks ?? 0) !==
          (this.original_skill_snapshot.ticks ?? 0) ||
        (this.local_skill?.training ?? 0) !==
          (this.original_skill_snapshot.training ?? 0)
      )
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    buildAbsoluteUrl(path) {
      if (!path) {
        return ''
      }

      if (/^(https?:|data:|blob:)/i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
    //========================================================
    // mapped methods
    //----------------
    ...mapGetters('combat', {
      getConfig: 'getConfig',
    }),
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    refreshLocalSkillDerivedValues() {
      const activeSkill = this.local_skill

      if (isEmpty(activeSkill) || !activeSkill?.name) {
        return
      }

      if (!isEmpty(activeSkill.parent_name)) {
        const parentSkill = (this.character?.skills || []).find(
          (skill) => skill.name === activeSkill.parent_name,
        )

        if (parentSkill) {
          activeSkill.parent_cur = parentSkill.cur ?? 0
          activeSkill.parent_adjs_tot = parentSkill.adjs_tot ?? 0
        }
      }

      calcSkillValues({
        skill: activeSkill,
        character: this.character,
        max_skill: this.max_skill,
      })
    },
    //========================================================
    // display calc breakdown
    //------------------------
    displayCalcBreakdown(type) {
      let s = this.activeSkill
      if (!s || !s.name) return ''
      //-----------
      // max ticks
      //-----------
      if (type === 'ticks_max') {
        let breakdown = `Base (${s.base ?? 0})`
        breakdown += `\n+ Training (${s.training ?? 0})`
        breakdown += `\nConstrained by:`
        breakdown += `\nMax Skill (${this.max_skill}) -`
        breakdown += `\nCurrent Skill (No Adjs) (${s.cur_no_adjs ?? 0})`
        return breakdown
      }
      //-----------------
      // remaining ticks
      //-----------------
      if (type === 'ticks_remaining') {
        let breakdown = `Max Ticks (${s.ticks_max ?? 0})`
        breakdown += `\n- Current Ticks (${s.ticks ?? 0})`
        return breakdown
      }
      // -----------
      // base skill
      // -----------
      if (type === 'base') {
        let args = {}
        if (!isEmpty(s.parent_name)) {
          args = {
            arg1: s.parent_formula_arg1,
            arg2: s.parent_formula_arg2,
            arg3: s.parent_formula_arg3,
            oper1: s.parent_formula_oper1,
            oper2: s.parent_formula_oper2,
          }
        } else {
          args = {
            arg1: s.formula_arg1,
            arg2: s.formula_arg2,
            arg3: s.formula_arg3,
            oper1: s.formula_oper1,
            oper2: s.formula_oper2,
          }
        }
        // get populated skill base
        return getPopulatedSkillBase(args, this.character)
      }
      // ------------
      // cur no adjs
      // ------------
      if (type === 'cur_no_adjs') {
        const args = {
          base: s.base,
          ticks: s.ticks,
          training: s.training,
        }
        // get populated skill cur no adjs
        return getPopulatedSkillCurNoAdjs(args, this.character)
      }
      // ----
      // cur
      // ----
      if (type === 'cur') {
        const args = {
          ticks: s.ticks,
          training: s.training,
          adjs_tot: s.adjs_tot,
        }
        // if child skill - pass parent_cur
        if (!isEmpty(s.parent_name)) {
          args['parent_name'] = s.parent_name
          args['parent_cur'] = s.parent_cur
        } else {
          // else pass base
          args['base'] = s.base
        }
        // get populated skill cur no adjs
        return getPopulatedSkillCurrent(args, this.character)
      }
      // ---------
      // increase
      // ---------
      if (type === 'incr') {
        return getPopulatedSkillIncrease(
          s.cur_no_adjs,
          this.character.know_bonus_percent,
        )
      }
    },
    //========================================================
    // on counter changed
    //--------------------
    onCounterChanged({ ref, count }) {
      console.log(
        'onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
        ' newcharacter = ',
        this.isNewSkill,
      )
      if (!ref) {
        console.warn(
          'SkillCard.onCounterChanged ignoring payload without ref/count',
        )
        return
      }
      //---------------------------
      // ensure local skill exists
      //---------------------------
      this.local_skill = this.local_skill || {}
      this.local_skill[ref] = count
      //---------------------
      // recalc skill values
      //---------------------
      calcSkillValues({
        skill: this.local_skill,
        character: this.character,
        max_skill: this.max_skill,
      })

      const normalizedCount = Number(this.local_skill[ref] ?? count) || 0

      this.local_skill[ref] = normalizedCount
    },
    //========================================================
    // on skill select
    //-----------------
    onSkillSelect(name) {
      console.log('onSkillSelect name = ', name)
      //---------------------------
      // ensure local skill exists
      //---------------------------
      this.local_skill = this.local_skill || {}
      this.local_skill.name = name || ''

      if (!name) {
        this.local_skill.reference_document = null
        return
      }

      //===================================
      // A. STATIC VALUES
      //===================================
      console.log('this.new_skill_options = ', this.new_skill_options)
      //---------------------------------------
      // find selected static skill definition
      //---------------------------------------
      const selected_skill = this.new_skill_options.find((s) => s.name === name)
      let new_skill = selected_skill
      if (!selected_skill) {
        console.warn('selected skill not found in new_skill_options:', name)
        this.local_skill.reference_document = null
        return
      }
      //-------------------------
      // save id for add payload
      //-------------------------
      this.save_new_skill_id = new_skill.id
      console.log('save_new_skill_id = ', this.save_new_skill_id)
      this.local_skill.reference_document =
        selected_skill.reference_document ??
        selected_skill.parent?.reference_document ??
        null
      this.local_skill.description =
        selected_skill.description ?? selected_skill.parent?.description ?? ''
      //---------------------
      // handle parent skill
      //---------------------
      if (new_skill.parent) {
        new_skill = new_skill.parent

        //-----------------
        // set parent name
        //-----------------
        this.local_skill.parent_name = new_skill.name || ''
        console.log('set parent name = ', this.local_skill.parent_name)
        //------------------------------------
        // set training/ticks = parent values
        //------------------------------------
        const parentSkill = this.getCharacterSkills().find(
          (s) => s.name === new_skill.name,
        )

        if (parentSkill) {
          console.log('found parent :', parentSkill)
          this.local_skill.ticks = 0
          this.local_skill.training = 0
          this.local_skill.parent_cur = parentSkill.cur ?? 0
          this.local_skill.parent_adjs_tot = parentSkill.adjs_tot ?? 0
        }
      } else {
        //----------------
        // else no parent
        //----------------
        this.local_skill.parent_name = ''
      }
      //------------------
      // set formula/args
      //------------------
      this.local_skill.formula = new_skill.formula ?? ''
      this.local_skill.formula_arg1 = new_skill.formula_arg1 ?? ''
      this.local_skill.formula_arg2 = new_skill.formula_arg2 ?? ''
      this.local_skill.formula_arg3 = new_skill.formula_arg3 ?? ''
      this.local_skill.formula_oper1 = new_skill.formula_oper1 ?? ''
      this.local_skill.formula_oper2 = new_skill.formula_oper2 ?? ''
      //--------------
      // set tickable
      //--------------
      this.local_skill.tickable = new_skill.tickable ?? false
      //===================================
      // B. CALCULATED VALUES
      //===================================
      calcSkillValues({
        skill: this.local_skill,
        character: this.character,
        max_skill: this.max_skill,
      })
    },
    //========================================================
    // get dropdown values
    //---------------------
    getDropDownValues() {
      console.log('getDropDownValues called for activeTab = ', this.activeTab)
      this.new_skill_options = []
      //---------------------------------------
      // where common tab - get static common
      //---------------------------------------
      if (this.activeTab === 'skills-common') {
        this.new_skill_options = this.getStaticCommon
        //-------------------------------------------
        // where uncommon tab - get static uncommon
        //-------------------------------------------
      } else if (this.activeTab === 'skills-uncommon') {
        this.new_skill_options = this.getStaticUncommon
        //-----------------------------------------------
        // where specialism tab - get static specialisms
        //-----------------------------------------------
      } else if (this.activeTab === 'skills-specialisms') {
        this.new_skill_options = this.getStaticSpecialisms
        //-------------------------------------------
        // where language tab - get static languages
        //-------------------------------------------
      } else if (this.activeTab === 'skills-languages') {
        this.new_skill_options = this.getStaticLanguages
        //-----------------------------------
        // where lore tab - get static lores
        //-----------------------------------
      } else if (this.activeTab === 'skills-lores') {
        this.new_skill_options = this.getStaticLores
        //----------------------------------------------------
        // where melee weapons tab - get static melee weapons
        //----------------------------------------------------
      } else if (this.activeTab === 'skills-melee-weapons') {
        this.new_skill_options = this.getStaticMeleeWeapons
        //------------------------------------------------------
        // where ranged weapons tab - get static ranged weapons
        //------------------------------------------------------
      } else if (this.activeTab === 'skills-ranged-weapons') {
        this.new_skill_options = this.getStaticRangedWeapons
        //----------------------------------------
        // where casting tab - get static casting
        //----------------------------------------
      } else if (this.activeTab === 'skills-casting') {
        this.new_skill_options = this.getStaticCasting
      }
      //---------------------------
      // where skill options found
      //---------------------------
      if (this.new_skill_options && this.new_skill_options.length > 0) {
        console.log('found new_skill_options = ', this.new_skill_options)
        //-----------------
        // get skill names
        //-----------------
        this.new_skill_option_names = extractNames(this.new_skill_options)
        //-----------------------------------
        // exclude existing character skills
        //-----------------------------------
        this.new_skill_option_names = this.new_skill_option_names.filter(
          (name) => {
            return !this.character.skills.some((skill) => skill.name === name)
          },
        )
        console.log('>>> new_skill_options = ', this.new_skill_options)
      }
    },
    //=====================================================================
    // confirm deletion
    //------------------
    async confirmDeletion() {
      if (!this.canDeleteSkill) {
        this.delete = false
        return
      }

      this.delete = true
      const ok = await this.$refs.confirmDialog.show({
        title: 'Delete Character Skill',
        message: 'Confirm Deletion',
        subject: this.skillName,
        okButton: 'OK',
        cancelButton: 'Cancel',
      })
      if (ok) {
        this.$emit('delete-character-skill', this.activeSkill)
        this.closeSidePanel()
      } else {
        this.delete = false
      }
    },
    //========================================================
    // on submit
    //-----------
    onSubmit() {
      if (this.submitCommitted) {
        return
      }

      console.log('---------------------------------')
      console.log('onSubmit')
      //----------------------------------------------------------
      // existing skill changes are limted to ticks/training only.
      // these are handled via emitUpdate on counter change.
      // OK confirms the edit and shows the update message.
      // delete is handled separately.
      //---------------------------------------------------------
      this.errors = []
      this.errorFields = []
      //--------------------------------------------------------
      // where adding new skill (i.e. specialism/language/lore)
      //--------------------------------------------------------
      let shouldCloseSidePanel = false

      if (this.isNewSkill) {
        ;``
        //
        const active = this.activeSkill
        const name = active?.name ?? ''
        const origName = this.original_skill_name ?? ''
        //-----------------------------------------------
        // check skill name not existing character skill
        //-----------------------------------------------
        console.log('active name = ', name)
        console.log('original_skill_name = ', origName)

        if (name !== origName) {
          console.log('CHECKING FOR EXISTING SKILL NAME')
          const exists = (this.character?.skills ?? []).some(
            (s) => s.name === name && s.id !== (this.skill?.id ?? 0),
          )
          console.log('exists = ', exists)

          // should not happen as existing skills are filtered out of drop-down
          if (exists) {
            this.errors.push('Character skill already exists.')
            this.errorFields.push('name')
          }
        }
        //-----------------------
        // continue if no errors
        //-----------------------
        if (this.errors.length === 0) {
          console.log('onSubmit....')
          this.submitCommitted = true

          // get editable fields (ticks/training)
          const ticks = this.activeSkill?.ticks ?? 0
          const training = this.activeSkill?.training ?? 0

          const add_parms = {
            name: name,
            payload: {
              id: createTempId(),
              character: this.character.id ? this.character.id : null,
              skill: this.save_new_skill_id,
              ticks: ticks,
              training: training,
            },
          }
          //----------------
          // emit add event
          //----------------
          console.log('emitting add-character-skill with parms = ', add_parms)
          this.$emit('add-character-skill', add_parms)
          shouldCloseSidePanel = true
        }
      } else if (this.isChanged) {
        this.submitCommitted = true

        if (
          (this.local_skill?.ticks ?? 0) !==
          (this.original_skill_snapshot.ticks ?? 0)
        ) {
          this.emitUpdate('ticks', this.local_skill?.ticks ?? 0)
        }

        if (
          (this.local_skill?.training ?? 0) !==
          (this.original_skill_snapshot.training ?? 0)
        ) {
          this.emitUpdate('training', this.local_skill?.training ?? 0)
        }

        this.$emit('update-character-skill', {
          type: 'skills',
          id: this.activeSkill?.id ?? this.local_skill.id ?? null,
          name: this.activeSkill?.name ?? this.local_skill.name ?? '',
          notifyOnly: true,
          action: 'updated',
        })
        shouldCloseSidePanel = true
      }

      if (shouldCloseSidePanel) {
        this.closeSidePanel()
      }
    },
    //========================================================
    // manage cancel
    //---------------
    manageCancel() {
      this.submitCommitted = false

      //-------------------------------------
      // where existing skill & changes made
      //-------------------------------------
      if (!this.isNewSkill && this.isChanged) {
        this.local_skill.ticks = this.original_skill_snapshot.ticks
        this.local_skill.training = this.original_skill_snapshot.training
        this.refreshLocalSkillDerivedValues()
      }
      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },
    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.$emit('close-skill-panel')
    },
    openDocument() {
      if (!this.documentViewerUrl) {
        return
      }

      window.open(this.documentViewerUrl, '_blank', 'noopener,noreferrer')
    },
    //========================================================
    // force initial focus
    //---------------------
    forceInitialFocus() {
      this.editingMode = true
      this.$nextTick(() => {
        this.$refs.input.focus()
      })
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      if (!key) {
        console.warn('SkillCard.emitUpdate ignoring update without key')
        return
      }
      //------------------------------
      // set additional args for emit
      //------------------------------
      const name = this.activeSkill?.name ?? this.local_skill.name ?? '' // for debug
      const id = this.activeSkill?.id ?? this.local_skill.id ?? null // for skill processing (belt and braces)
      //-------------
      // emit update
      //-------------
      console.log(
        'emitUpdate key = ',
        key,
        ' value = ',
        value,
        ' name = ',
        name,
        ' id = ',
        id,
      )
      this.$emit('update-character-skill', {
        type: 'skills',
        key,
        value,
        name,
        id,
        suppressNotification: true,
      })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    /*
    Skill with Parent:
   
    - base = same for both basd on formula
    - ticks = parent ticks
    - training = parent training
    - cur = calculated from parent cur + child adjs
    - incr_percent = N/A as not directly tickable 
    */
    console.log('---------------------------------')
    console.log('>>> activeTab = ', this.activeTab)
    //------------
    // get config
    //------------
    this.config = this.getConfig()
    //---------------------
    // get dropdown values
    //---------------------
    this.getDropDownValues()
    //----------------
    // existing skill
    //----------------
    if (this.skill) {
      console.log('EXISTING SKILL...')
      //----------------------------------------
      // save name of unique check (new skills)
      //----------------------------------------
      this.original_skill_name = this.skill.name
      //----------------------------------------------------------
      // save editable fields snapshot for isChanged (and cancel)
      //----------------------------------------------------------
      this.original_skill_snapshot = {
        ticks: this.skill.ticks ?? 0,
        training: this.skill.training ?? 0,
      }
      //--------------------------------------------------------
      // copy skill to local skill for v-model (ticks/training)
      //--------------------------------------------------------
      this.local_skill = copyObjectNoRef(this.skill) || {}
      this.refreshLocalSkillDerivedValues()

      console.log('this.local_skill = ', this.local_skill)
    } else {
      //-----------
      // new skill
      //-----------
      this.local_skill = setSkillValues()
      this.refreshLocalSkillDerivedValues()
      console.log('NEW SKILL...')
    }
  },
}
</script>

<style scoped>
.form-floating textarea.bb-skill-description-control {
  min-height: 15em !important;
  height: auto !important;
}
</style>
